import { collection, addDoc, query, orderBy, limit, where, onSnapshot, getDocs, Timestamp, doc, updateDoc } from "firebase/firestore";
import { db, auth } from "../utils/firebase";
const AUTH_LOGS_COLLECTION = "authLogs";
const SUSPICIOUS_ACTIVITY_COLLECTION = "suspiciousActivity";
const MAX_FAILED_ATTEMPTS = 5; // Threshold for suspicious activity
const FAILED_ATTEMPT_WINDOW_MINUTES = 10; // Time window for failed attempts
/**
 * Log an authentication event to Firestore
 */
export const logAuthEvent = async (authLog) => {
    try {
        // Add IP address and user agent if available from browser
        const enhancedLog = {
            ...authLog,
            timestamp: Timestamp.now(),
            // Only trust backend-provided IP address
            ipAddress: authLog.ipAddress,
            userAgent: navigator.userAgent || undefined,
        };
        const docRef = await addDoc(collection(db, AUTH_LOGS_COLLECTION), enhancedLog);
        // Check for suspicious activity if this is a failed login
        if (authLog.eventType === 'login_failure') {
            await checkForSuspiciousActivity(authLog.email);
        }
        return docRef.id;
    }
    catch (error) {
        console.error("Error logging auth event:", error);
        throw error;
    }
};
/**
 * Check for suspicious activity based on failed login attempts
 */
const checkForSuspiciousActivity = async (email) => {
    try {
        // Calculate time window for failed attempts
        const windowStart = new Date();
        windowStart.setMinutes(windowStart.getMinutes() - FAILED_ATTEMPT_WINDOW_MINUTES);
        // Query for failed login attempts within the time window
        const q = query(collection(db, AUTH_LOGS_COLLECTION), where("email", "==", email), where("eventType", "==", "login_failure"), where("timestamp", ">=", Timestamp.fromDate(windowStart)), orderBy("timestamp", "desc"));
        const querySnapshot = await getDocs(q);
        // If failed attempts exceed threshold, log suspicious activity
        if (querySnapshot.size >= MAX_FAILED_ATTEMPTS) {
            await addDoc(collection(db, SUSPICIOUS_ACTIVITY_COLLECTION), {
                email,
                type: "multiple_failed_logins",
                count: querySnapshot.size,
                timestamp: Timestamp.now(),
                ipAddresses: Array.from(new Set(querySnapshot.docs.map(doc => doc.data().ipAddress).filter(Boolean))),
                details: `${querySnapshot.size} failed login attempts within ${FAILED_ATTEMPT_WINDOW_MINUTES} minutes`,
                resolved: false
            });
            // In a real application, you might trigger alerts or notifications here
            console.warn(`Suspicious activity detected: Multiple failed login attempts for ${email}`);
        }
    }
    catch (error) {
        console.error("Error checking for suspicious activity:", error);
    }
};
/**
 * Get auth logs with optional filtering
 */
export const getAuthLogs = async (filters, limitCount = 100) => {
    try {
        const constraints = [
            orderBy("timestamp", "desc"),
            limit(limitCount),
        ];
        // Add filters if provided
        if (filters?.startDate) {
            constraints.push(where("timestamp", ">=", Timestamp.fromDate(filters.startDate)));
        }
        if (filters?.endDate) {
            constraints.push(where("timestamp", "<=", Timestamp.fromDate(filters.endDate)));
        }
        if (filters?.eventType && filters.eventType !== "all") {
            constraints.push(where("eventType", "==", filters.eventType));
        }
        if (filters?.status && filters.status !== "all") {
            constraints.push(where("status", "==", filters.status));
        }
        if (filters?.userId) {
            constraints.push(where("userId", "==", filters.userId));
        }
        if (filters?.email) {
            constraints.push(where("email", "==", filters.email));
        }
        const q = query(collection(db, AUTH_LOGS_COLLECTION), ...constraints);
        const querySnapshot = await getDocs(q);
        const logs = [];
        querySnapshot.forEach((doc) => {
            logs.push({
                id: doc.id,
                ...doc.data(),
            });
        });
        // Apply search filter client-side (Firestore doesn't support text search)
        if (filters?.search) {
            const searchTerm = filters.search.toLowerCase();
            return logs.filter((log) => log.email.toLowerCase().includes(searchTerm) ||
                log.details?.toLowerCase().includes(searchTerm) ||
                log.error?.toLowerCase().includes(searchTerm));
        }
        return logs;
    }
    catch (error) {
        console.error("Error fetching auth logs:", error);
        throw error;
    }
};
/**
 * Subscribe to auth logs with real-time updates
 */
export const subscribeToAuthLogs = (callback, filters, limitCount = 100) => {
    try {
        const constraints = [
            orderBy("timestamp", "desc"),
            limit(limitCount),
        ];
        // Add filters if provided
        if (filters?.startDate) {
            constraints.push(where("timestamp", ">=", Timestamp.fromDate(filters.startDate)));
        }
        if (filters?.endDate) {
            constraints.push(where("timestamp", "<=", Timestamp.fromDate(filters.endDate)));
        }
        if (filters?.eventType && filters.eventType !== "all") {
            constraints.push(where("eventType", "==", filters.eventType));
        }
        if (filters?.status && filters.status !== "all") {
            constraints.push(where("status", "==", filters.status));
        }
        if (filters?.userId) {
            constraints.push(where("userId", "==", filters.userId));
        }
        if (filters?.email) {
            constraints.push(where("email", "==", filters.email));
        }
        const q = query(collection(db, AUTH_LOGS_COLLECTION), ...constraints);
        return onSnapshot(q, (querySnapshot) => {
            const logs = [];
            querySnapshot.forEach((doc) => {
                logs.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });
            // Apply search filter client-side
            if (filters?.search) {
                const searchTerm = filters.search.toLowerCase();
                const filteredLogs = logs.filter((log) => log.email.toLowerCase().includes(searchTerm) ||
                    log.details?.toLowerCase().includes(searchTerm) ||
                    log.error?.toLowerCase().includes(searchTerm));
                callback(filteredLogs);
            }
            else {
                callback(logs);
            }
        });
    }
    catch (error) {
        console.error("Error subscribing to auth logs:", error);
        throw error;
    }
};
/**
 * Get suspicious activity logs
 */
export const getSuspiciousActivityLogs = async (resolved = false, limitCount = 100) => {
    try {
        const q = query(collection(db, SUSPICIOUS_ACTIVITY_COLLECTION), where("resolved", "==", resolved), orderBy("timestamp", "desc"), limit(limitCount));
        const querySnapshot = await getDocs(q);
        const logs = [];
        querySnapshot.forEach((doc) => {
            logs.push({
                id: doc.id,
                ...doc.data(),
            });
        });
        return logs;
    }
    catch (error) {
        console.error("Error fetching suspicious activity logs:", error);
        throw error;
    }
};
/**
 * Mark suspicious activity as resolved
 */
export const resolveSuspiciousActivity = async (id) => {
    try {
        const docRef = doc(db, SUSPICIOUS_ACTIVITY_COLLECTION, id);
        await updateDoc(docRef, {
            resolved: true,
            resolvedAt: Timestamp.now(),
            resolvedBy: auth.currentUser?.uid || "unknown"
        });
    }
    catch (error) {
        console.error("Error resolving suspicious activity:", error);
        throw error;
    }
};
/**
 * Get authentication activity metrics
 */
export const getAuthMetrics = async (days = 30) => {
    try {
        // Calculate start date
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        // Query for all auth events in the time period
        const q = query(collection(db, AUTH_LOGS_COLLECTION), where("timestamp", ">=", Timestamp.fromDate(startDate)), orderBy("timestamp", "desc"));
        const querySnapshot = await getDocs(q);
        // Process logs to calculate metrics
        const logs = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        // Calculate metrics
        const metrics = {
            totalEvents: logs.length,
            successfulLogins: logs.filter(log => log.eventType === 'login_success').length,
            failedLogins: logs.filter(log => log.eventType === 'login_failure').length,
            registrations: logs.filter(log => log.eventType === 'register').length,
            passwordResets: logs.filter(log => log.eventType === 'password_reset_complete').length,
            adminActions: logs.filter(log => log.adminAction === true).length,
            eventsByType: {},
            dailyActivity: {},
        };
        // Count events by type
        logs.forEach(log => {
            if (log.eventType) {
                const eventType = log.eventType;
                metrics.eventsByType[eventType] = (metrics.eventsByType[eventType] || 0) + 1;
                // Group by day for daily activity chart
                if (log.timestamp) {
                    const date = new Date(log.timestamp.toDate());
                    const dateString = date.toISOString().split('T')[0];
                    metrics.dailyActivity[dateString] = (metrics.dailyActivity[dateString] || 0) + 1;
                }
            }
        });
        return metrics;
    }
    catch (error) {
        console.error("Error calculating auth metrics:", error);
        throw error;
    }
};
